// Options popup — structure/classes matched to the reference games' ui/
// Settings.jsx: a `.settings` panel with toggle rows, an account section
// (points + sign-in/out) and an in-place account-deletion view
// (main → delete → deleted) inside the same popup.

import { useEffect, useState } from "react";
import { LANGUAGES, getLanguage, setLanguage, useT, type LangCode } from "../i18n.ts";
import { useAuth } from "../auth/AuthContext.tsx";
import {
  getReducedMotion, getSoundOn, setReducedMotion, setSoundOn,
} from "../settings.ts";
import { getPoints } from "../points.ts";

export default function Settings({ onClose }: { onClose: () => void }) {
  const t = useT();
  const { user, configured, openEmailAuth, signOutUser, deleteAccount } = useAuth();
  const [soundOn, setSoundOnState] = useState(() => getSoundOn());
  const [reduced, setReducedState] = useState(() => getReducedMotion());
  const [language, setLanguageState] = useState<LangCode>(() => getLanguage());
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");

  const [view, setView] = useState<"main" | "delete" | "deleted">("main");
  const [delPassword, setDelPassword] = useState("");
  const [delAgree, setDelAgree] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [delError, setDelError] = useState("");

  useEffect(() => {
    if (view !== "delete") return undefined;
    const stop = (e: KeyboardEvent) => e.stopImmediatePropagation();
    window.addEventListener("keydown", stop, true);
    window.addEventListener("keyup", stop, true);
    return () => {
      window.removeEventListener("keydown", stop, true);
      window.removeEventListener("keyup", stop, true);
    };
  }, [view]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOnState(next);
    setSoundOn(next);
  };
  const toggleReduced = () => {
    const next = !reduced;
    setReducedState(next);
    setReducedMotion(next);
  };

  const handleSignIn = () => {
    setAuthError("");
    try {
      openEmailAuth();
    } catch (e) {
      setAuthError((e as Error)?.message || t("authError"));
    }
  };
  const handleSignOut = async () => {
    setAuthBusy(true);
    try {
      await signOutUser();
    } finally {
      setAuthBusy(false);
    }
  };

  const openDelete = () => {
    setDelPassword("");
    setDelAgree(false);
    setDelError("");
    setView("delete");
  };
  const handleDelete = async () => {
    if (!delPassword) {
      setDelError(t("password"));
      return;
    }
    setDelBusy(true);
    setDelError("");
    try {
      await deleteAccount(delPassword);
      setView("deleted");
    } catch (e) {
      setDelError((e as Error)?.message || t("authError"));
    } finally {
      setDelBusy(false);
    }
  };

  if (view === "delete") {
    return (
      <div className="settings">
        <h2 className="settings-title">{t("deleteAccount")}</h2>
        <p className="settings-hint">{t("deleteConfirm")}</p>
        <p className="settings-delete-target">{user?.email || user?.displayName}</p>
        <label className="settings-delete-field">
          <span className="settings-label">{t("password")}</span>
          <input
            type="password"
            value={delPassword}
            onChange={(e) => setDelPassword(e.target.value)}
            autoComplete="current-password"
            disabled={delBusy}
          />
        </label>
        <label className="settings-delete-agree">
          <input
            type="checkbox"
            checked={delAgree}
            onChange={(e) => setDelAgree(e.target.checked)}
            disabled={delBusy}
          />
          <span>{t("deleteAgree")}</span>
        </label>
        {delError && <p className="settings-account-error">⚠ {delError}</p>}
        <button
          type="button"
          className="settings-account-btn danger"
          onClick={handleDelete}
          disabled={delBusy || !delAgree || !delPassword}
        >
          {delBusy ? "…" : t("deleteAccount")}
        </button>
        <button
          type="button"
          className="settings-close"
          onClick={() => setView("main")}
          disabled={delBusy}
        >
          {t("cancel")}
        </button>
      </div>
    );
  }

  if (view === "deleted") {
    return (
      <div className="settings">
        <h2 className="settings-title">{t("deleteDoneTitle")}</h2>
        <p className="settings-hint">{t("deleteDoneBody")}</p>
        <button className="settings-close" onClick={onClose}>{t("close")}</button>
      </div>
    );
  }

  return (
    <div className="settings">
      <h2 className="settings-title">{t("settings")}</h2>

      <div className="settings-row">
        <label className="settings-label" htmlFor="language-select">{t("language")}</label>
        <select
          id="language-select"
          className="settings-select"
          value={language}
          onChange={(e) => {
            const l = e.target.value as LangCode;
            setLanguageState(l);
            setLanguage(l);
          }}
        >
          {LANGUAGES.map((item) => (
            <option key={item.code} value={item.code}>{item.label}</option>
          ))}
        </select>
      </div>

      <div className="settings-row">
        <span className="settings-label">{t("sound")}</span>
        <button
          type="button"
          role="switch"
          aria-checked={soundOn}
          className={`settings-switch${soundOn ? " on" : ""}`}
          onClick={toggleSound}
        >
          <span className="settings-knob" />
        </button>
      </div>

      <div className="settings-row">
        <span className="settings-label">{t("reducedMotion")}</span>
        <button
          type="button"
          role="switch"
          aria-checked={reduced}
          className={`settings-switch${reduced ? " on" : ""}`}
          onClick={toggleReduced}
        >
          <span className="settings-knob" />
        </button>
      </div>

      <div className="settings-account">
        <div className="settings-account-head">
          <span className="settings-label">{t("account")}</span>
          <span className="settings-points">{getPoints().toLocaleString()} P</span>
        </div>
        {!configured ? (
          <p className="settings-hint">{t("authSetupNeeded")}</p>
        ) : user ? (
          <>
            <p className="settings-account-user">
              {user.displayName || user.email || t("signedInAs")}
            </p>
            <button
              type="button"
              className="settings-account-btn"
              onClick={handleSignOut}
              disabled={authBusy}
            >
              {t("logout")}
            </button>
            <p className="settings-hint">{t("pointsSyncAccount")}</p>
          </>
        ) : (
          <>
            <button
              type="button"
              className="settings-account-btn primary"
              onClick={handleSignIn}
              disabled={authBusy}
            >
              {t("login")}
            </button>
            <p className="settings-hint">{t("pointsSyncSignIn")}</p>
          </>
        )}
        {authError && <p className="settings-account-error">⚠ {authError}</p>}
        <p className="legal-links">
          <a href="https://gamzone.co.kr/privacy" target="_blank" rel="noopener noreferrer">
            {t("privacy")}
          </a>
          {configured && user && (
            <>
              {" | "}
              <button type="button" onClick={openDelete}>{t("deleteAccount")}</button>
            </>
          )}
        </p>
      </div>

      <button className="settings-close" onClick={onClose}>{t("close")}</button>
    </div>
  );
}
